import React, { useMemo, memo } from 'react';

const keyframeCSS = `
  @media (max-width: 768px) {
    .prize-board-container {
      max-width: 100% !important;
      padding: 8px 4px !important;
      gap: 4px !important;
    }
    .prize-board-title {
      font-size: 0.65rem !important;
      margin-bottom: 4px !important;
      padding: 4px 0 !important;
    }
    .prize-board-columns {
      gap: 4px !important;
    }
    .prize-board-row {
      padding: 3px 6px !important;
      font-size: 0.65rem !important;
      gap: 4px !important;
    }
    .prize-board-dot {
      width: 4px !important;
      height: 4px !important;
    }
    .prize-board-label {
      font-size: 0.55rem !important;
      margin-bottom: 2px !important;
    }
  }
  @keyframes prizePulse {
    0%, 100% { box-shadow: inset 0 0 6px rgba(255,255,255,0.04), 0 0 4px rgba(0,0,0,0.2); }
    50% { box-shadow: inset 0 0 12px rgba(255,255,255,0.08), 0 0 8px rgba(0,0,0,0.1); }
  }
  @keyframes dotGlow {
    0%, 100% { box-shadow: 0 0 4px currentColor; }
    50% { box-shadow: 0 0 8px currentColor, 0 0 12px currentColor; }
  }
  @keyframes neonTitle {
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% { text-shadow: 0 0 10px rgba(212,175,55,0.6), 0 0 20px rgba(212,175,55,0.3), 0 0 40px rgba(212,175,55,0.15); }
    20%, 24%, 55% { text-shadow: 0 0 5px rgba(212,175,55,0.3); }
  }
  @keyframes eliminateFlash {
    0% { opacity: 1; }
    50% { opacity: 0.1; }
    100% { opacity: 0.3; }
  }
`;

function formatPrize(prize) {
  // If prize is an object with label and value
  if (prize && typeof prize === 'object' && 'label' in prize) {
    if (prize.value === 0 && prize.label) return prize.label;
    return formatEuro(prize.value);
  }
  // Legacy: plain number
  return formatEuro(prize);
}

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  if (value >= 1000) {
    return '\u20AC' + value.toLocaleString('it-IT');
  }
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export default function PrizeBoard({ boxes = [], allPrizes = [] }) {
  const prizeList = useMemo(() => {
    // Normalize prizes: support both plain numbers and {value, label} objects
    const normalized = allPrizes.map((p) =>
      typeof p === 'object' && p !== null ? { value: p.value ?? 0, label: p.label ?? null } : { value: p, label: null }
    );
    const sorted = [...normalized].sort((a, b) => a.value - b.value);

    // Build a key for matching: use label if text prize, else value
    const getKey = (p) => (p.value === 0 && p.label ? `text:${p.label}` : `val:${p.value}`);

    const eliminatedCounts = new Map();
    boxes.forEach((box) => {
      if (box.is_opened && (box.prize_value !== undefined || box.prize_label)) {
        const key = (box.prize_value === 0 || box.prize_value === undefined) && box.prize_label
          ? `text:${box.prize_label}` : `val:${box.prize_value}`;
        eliminatedCounts.set(key, (eliminatedCounts.get(key) || 0) + 1);
      }
    });

    const usedCounts = new Map();
    return sorted.map((prize) => {
      const key = getKey(prize);
      const elimCount = eliminatedCounts.get(key) || 0;
      const usedSoFar = usedCounts.get(key) || 0;
      const isEliminated = usedSoFar < elimCount;
      usedCounts.set(key, usedSoFar + 1);
      return { ...prize, eliminated: isEliminated };
    });
  }, [allPrizes, boxes]);

  const mid = Math.ceil(prizeList.length / 2);
  const lowPrizes = prizeList.slice(0, mid);
  const highPrizes = prizeList.slice(mid);

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '16px 12px',
    maxWidth: '300px',
    width: '100%',
  };

  const titleStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#D4AF37',
    textAlign: 'center',
    marginBottom: '10px',
    animation: 'neonTitle 3s ease-in-out infinite',
    padding: '8px 0',
    borderTop: '1px solid rgba(212,175,55,0.2)',
    borderBottom: '1px solid rgba(212,175,55,0.2)',
  };

  const columnsStyle = {
    display: 'flex',
    gap: '8px',
    width: '100%',
  };

  const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  };

  const columnLabelStyle = (color) => ({
    fontFamily: 'var(--font-display)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: color,
    textAlign: 'center',
    marginBottom: '6px',
    textShadow: `0 0 10px ${color}40`,
  });

  return (
    <div className="prize-board-container" style={containerStyle}>
      <style>{keyframeCSS}</style>

      <div className="prize-board-title" style={titleStyle}>
        PREMI IN GIOCO
      </div>

      <div className="prize-board-columns" style={columnsStyle}>
        {/* Low prizes - Blue */}
        <div style={columnStyle}>
          <div className="prize-board-label" style={columnLabelStyle('#3498db')}>Bassi</div>
          {lowPrizes.map((prize, i) => (
            <PrizeRow
              key={`low-${i}`}
              prize={prize}
              isLow={true}
              index={i}
            />
          ))}
        </div>

        {/* High prizes - Red */}
        <div style={columnStyle}>
          <div className="prize-board-label" style={columnLabelStyle('#e74c3c')}>Alti</div>
          {highPrizes.map((prize, i) => (
            <PrizeRow
              key={`high-${i}`}
              prize={prize}
              isLow={false}
              index={i}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const PrizeRow = memo(function PrizeRow({ prize, isLow, index }) {
  const eliminated = prize.eliminated;

  const activeColor = isLow ? '#3498db' : '#e74c3c';
  const activeBg = isLow
    ? 'linear-gradient(135deg, rgba(26,35,126,0.7), rgba(40,53,147,0.5))'
    : 'linear-gradient(135deg, rgba(183,28,28,0.7), rgba(198,40,40,0.5))';

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '5px',
    background: eliminated ? 'rgba(50,50,50,0.2)' : activeBg,
    border: eliminated
      ? '1px solid rgba(80,80,80,0.15)'
      : `1px solid ${activeColor}30`,
    fontFamily: 'var(--font-mono)',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: eliminated ? 'rgba(100,100,130,0.4)' : '#f0f0f5',
    textDecoration: eliminated ? 'line-through' : 'none',
    opacity: eliminated ? 0.35 : 1,
    animation: eliminated ? 'none' : 'prizePulse 3s ease-in-out infinite',
    animationDelay: `${index * 0.15}s`,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    justifyContent: isLow ? 'flex-start' : 'flex-end',
    flexDirection: isLow ? 'row' : 'row-reverse',
  };

  // Glowing dot indicator
  const dotStyle = {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: eliminated ? 'rgba(80,80,80,0.3)' : activeColor,
    flexShrink: 0,
    animation: eliminated ? 'none' : 'dotGlow 2s ease-in-out infinite',
    animationDelay: `${index * 0.1}s`,
    color: activeColor,
  };

  const valueStyle = {
    flex: 1,
    textAlign: isLow ? 'left' : 'right',
    whiteSpace: 'nowrap',
  };

  return (
    <div
      className="prize-board-row"
      style={rowStyle}
    >
      <div className="prize-board-dot" style={dotStyle} />
      <span style={valueStyle}>{formatPrize(prize)}</span>
    </div>
  );
});
